import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants';
import {
  AutomotiveAftermarketProfileItemCreate,
  AutomotiveAftermarketProfileItemInitialState,
  AutomotiveAftermarketProfileItemLoad,
  AutomotiveAftermarketProfileItemUpdate,
} from './automotive-aftermarket-profile-item.actions';
import { AutomotiveAftermarketProfileModel } from '../models';
import { AutomotiveAftermarketProfileItemService } from './automotive-aftermarket-profile-item.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AutomotiveAftermarketProfileItemStateModel extends AutomotiveAftermarketProfileModel {
  loaded: boolean;
}

const initialState: AutomotiveAftermarketProfileItemStateModel = {
  ...new AutomotiveAftermarketProfileModel(),
  loaded: false,
};

@State<AutomotiveAftermarketProfileItemStateModel>({
  name: 'automotiveAftermarketProfileItem',
  defaults: initialState,
})
@Injectable()
export class AutomotiveAftermarketProfileItemState {
  constructor(
    private automotiveAftermarketProfileItemService: AutomotiveAftermarketProfileItemService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getProfileId(state: AutomotiveAftermarketProfileItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getState(state: AutomotiveAftermarketProfileItemStateModel): AutomotiveAftermarketProfileItemStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: AutomotiveAftermarketProfileItemStateModel): boolean {
    return state.loaded;
  }

  @Action(AutomotiveAftermarketProfileItemLoad)
  getAutomotiveAftermarketProfileItem(
    ctx: StateContext<AutomotiveAftermarketProfileItemStateModel>,
    { payload }: AutomotiveAftermarketProfileItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.automotiveAftermarketProfileItemService.getAutomotiveAftermarketProfile(payload).pipe(
      map((automotiveAftermarketProfile: AutomotiveAftermarketProfileModel) => {
        ctx.patchState({
          ...new AutomotiveAftermarketProfileModel(automotiveAftermarketProfile),
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketProfileItemCreate)
  createAutomotiveAftermarketProfileItem(
    ctx: StateContext<AutomotiveAftermarketProfileItemStateModel>,
    { payload }: AutomotiveAftermarketProfileItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.automotiveAftermarketProfileItemService.createAutomotiveAftermarketProfile(payload.profile).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.automotiveAftermarketProfile.success.create');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketProfileItemUpdate)
  updateAutomotiveAftermarketProfileItem(
    ctx: StateContext<AutomotiveAftermarketProfileItemStateModel>,
    { payload }: AutomotiveAftermarketProfileItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.automotiveAftermarketProfileItemService.updateAutomotiveAftermarketProfile(payload).pipe(
      map((updatedProfile: AutomotiveAftermarketProfileModel) => {
        ctx.patchState({ ...updatedProfile });
        this.notificationService.success('api.automotiveAftermarketProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketProfileItemInitialState)
  setInitialState(ctx: StateContext<AutomotiveAftermarketProfileItemStateModel>): void {
    ctx.setState(initialState);
  }
}
